import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, FormControlLabel, IconButton, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography, Paper,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HistoryIcon from '@mui/icons-material/History';
import BarChartIcon from '@mui/icons-material/BarChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CardWrapper, SectionTitle, Field, formatBRL,
} from '../../UI';
import { RECEBIMENTOS, RESUMO_ACORDOS } from '../../../mocks/processos.mock';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Recebimento.
 *
 * Print `grid_check_recebimentos.png`: cabeçalho com Cliente/Devedor/Responsável,
 * o switch "Exibir apenas Acordos Ativos e Propostas", a tabela de acordos com
 * 6 ícones de ação por linha, e os 5 cards-contadores no rodapé.
 */
const ACOES = [
  { icone: <DescriptionOutlinedIcon fontSize="small" />, hint: 'Ver demonstrativo' },
  { icone: <ReplyIcon fontSize="small" />, hint: 'Reenviar' },
  { icone: <MarkEmailReadOutlinedIcon fontSize="small" />, hint: 'Confirmar envio' },
  { icone: <EmailOutlinedIcon fontSize="small" />, hint: 'Enviar por e-mail' },
  { icone: <HistoryIcon fontSize="small" />, hint: 'Histórico do acordo' },
  { icone: <BarChartIcon fontSize="small" />, hint: 'Estatísticas' },
];

export default function AbaRecebimento({ processo }) {
  const [apenasAtivos, setApenasAtivos] = useState(false);
  const [marcados, setMarcados] = useState(RESUMO_ACORDOS.map(r => r.label));

  const toggleResumo = label => setMarcados(m => (m.includes(label) ? m.filter(x => x !== label) : [...m, label]));

  return (
    <CardWrapper>
      <SectionTitle>Recebimentos</SectionTitle>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 0.8fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Field label="Cliente:" value="10" sx={{ width: 68, flexShrink: 0 }} InputProps={{ readOnly: true }} />
          <Field label="&nbsp;" value={processo?.nomeCliente || ''} InputProps={{ readOnly: true }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Field label="Devedor:" value="985678" sx={{ width: 92, flexShrink: 0 }} InputProps={{ readOnly: true }} />
          <Field label="&nbsp;" value={processo?.nomeDevedor || ''} InputProps={{ readOnly: true }} />
        </Box>
        <Field label="Responsável:" value={processo?.negociador || ''} InputProps={{ readOnly: true }} />
      </Box>

      <FormControlLabel
        control={<Switch checked={apenasAtivos} onChange={e => setApenasAtivos(e.target.checked)} />}
        label={<Typography variant="body2">Exibir apenas Acordos Ativos e Propostas</Typography>}
        sx={{ mb: 1 }}
      />

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Fase</TableCell>
              <TableCell>Negociador</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Formas de pagamento</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {RECEBIMENTOS.filter(r => !apenasAtivos || r.statusCor === 'warning').map(r => (
              <TableRow key={r.numero} hover>
                <TableCell sx={{ fontWeight: 600 }}>{r.numero}</TableCell>
                <TableCell>{r.data}</TableCell>
                <TableCell>{r.fase}</TableCell>
                <TableCell>{r.negociador}</TableCell>
                <TableCell align="center">
                  <Tooltip title={r.statusCor === 'error' ? 'Acordo quebrado' : 'Acordo ativo'} arrow>
                    <Box
                      sx={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: r.statusCor === 'error' ? C.tomato : C.tangerine,
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>{r.formaPagamento}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatBRL(r.valor)}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}>
                    {ACOES.map(a => (
                      <Tooltip key={a.hint} title={a.hint} arrow>
                        <IconButton size="small" sx={{ color: C.navy, p: 0.4 }}>{a.icone}</IconButton>
                      </Tooltip>
                    ))}
                    <Tooltip title="Mais" arrow>
                      <IconButton size="small" sx={{ color: C.navy, p: 0.4 }}>
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Cards-contadores */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'repeat(5, minmax(0, 1fr))' },
          gap: 'clamp(8px, 0.6vw, 16px)',
          mt: 2.5,
        }}
      >
        {RESUMO_ACORDOS.map(r => (
          <Paper
            key={r.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: 1.25,
              backgroundColor: C.paleGrey,
              boxShadow: 'none',
              border: `1px solid ${C.borderGrey}`,
            }}
          >
            <Checkbox size="small" checked={marcados.includes(r.label)} onChange={() => toggleResumo(r.label)} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{r.label}</Typography>
              <Typography sx={{ fontWeight: 700, color: C.navy }}>{r.total}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      <Typography sx={{ fontWeight: 700, mt: 2 }}>Total de acordos: 1</Typography>
    </CardWrapper>
  );
}
AbaRecebimento.propTypes = { processo: PropTypes.object };
AbaRecebimento.defaultProps = { processo: null };
